import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Card, CardBody, Spinner, Table, Badge } from 'reactstrap';
import usePermission from '../Hooks/usePermission';
import { fetchAssetTCO, clearAssetTCO } from '../store/assetTCO/actions';
import { formatCurrency } from '../helpers/formatCurrency';

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${d.getFullYear()}`;
};

const toNum = (v) => {
  if (v === null || v === undefined) return 0;
  const n = typeof v === 'string' ? parseFloat(v) : Number(v);
  return Number.isNaN(n) ? 0 : n;
};

/**
 * Admin-only Total Cost of Ownership panel for an asset.
 *
 * Three sections:
 *  - Acquisition: purchase price + commissioning + estimated lifespan
 *  - Depreciation: accumulated + net book value (from existing fields, informational)
 *  - Maintenance & TCO: per-bucket breakdown + grand total
 *
 * Renders nothing if the user lacks {@code cost:read-asset-tco}.
 */
const AssetTCOPanel = ({ assetId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { hasPermission } = usePermission();

  const tco = useSelector((s) => s.AssetTCO?.tco);
  const loading = useSelector((s) => s.AssetTCO?.loading);
  const error = useSelector((s) => s.AssetTCO?.error);

  const canRead = hasPermission('cost:read-asset-tco');

  const [includeDowntime, setIncludeDowntime] = useState(true);

  const { displayedMaintenanceSubtotal, displayedTotalTCO } = useMemo(() => {
    if (!tco) return { displayedMaintenanceSubtotal: null, displayedTotalTCO: null };
    const downtime = toNum(tco.cumulativeDowntimeCost);
    const maintenance = toNum(tco.cumulativeMaintenanceCost);
    const total = toNum(tco.totalTCO);
    return {
      displayedMaintenanceSubtotal: includeDowntime ? tco.cumulativeMaintenanceCost : maintenance - downtime,
      displayedTotalTCO: includeDowntime ? tco.totalTCO : total - downtime,
    };
  }, [tco, includeDowntime]);

  useEffect(() => {
    if (!canRead || !assetId) return;
    dispatch(fetchAssetTCO(assetId));
    return () => dispatch(clearAssetTCO());
  }, [dispatch, canRead, assetId]);

  if (!canRead) return null;

  return (
    <Card className="border-0 shadow-sm mb-3">
      <CardBody>
        <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap" style={{ gap: '8px' }}>
          <div>
            <h5 className="mb-1">
              <i className="bx bx-pie-chart-alt-2 me-2 text-primary"></i>
              {t('cost_tracking.asset_tco.title', 'Total Cost of Ownership')}
            </h5>
            <small className="text-muted">
              {t('cost_tracking.asset_tco.subtitle', 'Acquisition + cumulative maintenance over the asset lifetime')}
            </small>
          </div>
          <div className="d-flex align-items-center flex-wrap" style={{ gap: '12px' }}>
            {tco?.replacementSuggested && (
              <Badge color="danger" className="d-flex align-items-center">
                <i className="bx bx-error-circle me-1"></i>
                {t('cost_tracking.asset_tco.replacement_suggested', 'Replacement Suggested')}
              </Badge>
            )}
            {tco?.workOrdersInScope != null && (
              <Badge color="light" className="text-dark border">
                {t('cost_tracking.asset_tco.wo_count', '{{count}} work order(s) counted', { count: tco.workOrdersInScope })}
              </Badge>
            )}
            <div className="form-check form-switch mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                id="includeDowntimeSwitch"
                checked={includeDowntime}
                onChange={(e) => setIncludeDowntime(e.target.checked)}
              />
              <label className="form-check-label small" htmlFor="includeDowntimeSwitch">
                {t('cost_tracking.asset_tco.include_downtime', 'Include downtime cost')}
              </label>
            </div>
          </div>
        </div>

        {loading && (
          <div className="text-center py-3">
            <Spinner size="sm" /> {t('cost_tracking.asset_tco.loading', 'Loading…')}
          </div>
        )}
        {error && <div className="alert alert-danger">{error}</div>}

        {!loading && tco && (
          <>
            {tco.hasMissingPriceData && (
              <div className="alert alert-warning small">
                {t('cost_tracking.asset_tco.missing_price_data',
                  'Some inputs are missing — TCO may be understated.')}
              </div>
            )}

            <div className="row g-3">
              {/* Acquisition */}
              <div className="col-md-4">
                <div className="info-card bg-light rounded p-3 h-100">
                  <h6 className="text-primary mb-3 fw-semibold">
                    <i className="bx bx-receipt me-2"></i>
                    {t('cost_tracking.asset_tco.acquisition', 'Acquisition')}
                  </h6>
                  <div className="info-item mb-2">
                    <div className="text-muted small">{t('cost_tracking.asset_tco.purchase_price', 'Purchase price')}</div>
                    <div className="fw-medium">{formatCurrency(tco.assetCost, tco.currency)}</div>
                  </div>
                  <div className="info-item mb-2">
                    <div className="text-muted small">{t('cost_tracking.asset_tco.commissioning_date', 'Commissioned')}</div>
                    <div className="fw-medium">{formatDate(tco.commissioningDate)}</div>
                  </div>
                  <div className="info-item">
                    <div className="text-muted small">{t('cost_tracking.asset_tco.estimated_lifespan', 'Estimated lifespan')}</div>
                    <div className="fw-medium">
                      {tco.estimatedLifespan ? `${tco.estimatedLifespan} ${t('shared.years', 'years')}` : '—'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Depreciation */}
              <div className="col-md-4">
                <div className="info-card bg-light rounded p-3 h-100">
                  <h6 className="text-success mb-3 fw-semibold">
                    <i className="bx bx-trending-down me-2"></i>
                    {t('cost_tracking.asset_tco.depreciation', 'Depreciation')}
                  </h6>
                  <div className="info-item mb-2">
                    <div className="text-muted small">{t('cost_tracking.asset_tco.accumulated', 'Accumulated')}</div>
                    <div className="fw-medium">{formatCurrency(tco.accumulatedDepreciation, tco.currency)}</div>
                  </div>
                  <div className="info-item">
                    <div className="text-muted small">{t('cost_tracking.asset_tco.book_value', 'Net book value')}</div>
                    <div className="fw-medium">{formatCurrency(tco.netBookValue, tco.currency)}</div>
                  </div>
                </div>
              </div>

              {/* Maintenance breakdown */}
              <div className="col-md-4">
                <div className="info-card bg-light rounded p-3 h-100">
                  <h6 className="text-warning mb-3 fw-semibold">
                    <i className="bx bx-wrench me-2"></i>
                    {t('cost_tracking.asset_tco.maintenance', 'Maintenance')}
                  </h6>
                  <Table size="sm" borderless className="mb-0">
                    <tbody>
                      <tr>
                        <td className="ps-0 py-1">{t('cost_tracking.wo_total.labor', 'Labor')}</td>
                        <td className="text-end py-1">{formatCurrency(tco.cumulativeLaborCost, tco.currency)}</td>
                      </tr>
                      <tr>
                        <td className="ps-0 py-1">{t('cost_tracking.wo_total.parts', 'Parts')}</td>
                        <td className="text-end py-1">{formatCurrency(tco.cumulativePartsCost, tco.currency)}</td>
                      </tr>
                      <tr>
                        <td className="ps-0 py-1">{t('cost_tracking.wo_total.contractor', 'Contractor')}</td>
                        <td className="text-end py-1">{formatCurrency(tco.cumulativeContractorCost, tco.currency)}</td>
                      </tr>
                      <tr className={includeDowntime ? '' : 'text-muted'} style={!includeDowntime ? { textDecoration: 'line-through' } : undefined}>
                        <td className="ps-0 py-1">{t('cost_tracking.wo_total.downtime', 'Downtime')}</td>
                        <td className="text-end py-1">{formatCurrency(tco.cumulativeDowntimeCost, tco.currency)}</td>
                      </tr>
                      <tr className="border-top fw-bold">
                        <td className="ps-0 py-1">{t('cost_tracking.asset_tco.subtotal', 'Subtotal')}</td>
                        <td className="text-end py-1">{formatCurrency(displayedMaintenanceSubtotal, tco.currency)}</td>
                      </tr>
                    </tbody>
                  </Table>
                </div>
              </div>
            </div>

            {/* Grand total */}
            <div className="bg-primary bg-opacity-10 rounded p-3 mt-3 d-flex justify-content-between align-items-center">
              <div>
                <div className="text-muted small">{t('cost_tracking.asset_tco.total_label', 'Total Cost of Ownership')}</div>
                <small className="text-muted">{t('cost_tracking.asset_tco.total_formula', 'Purchase price + cumulative maintenance')}</small>
              </div>
              <h4 className="mb-0 text-primary fw-bold">{formatCurrency(displayedTotalTCO, tco.currency)}</h4>
            </div>

            {tco.replacementSuggested && (
              <div className="alert alert-danger mt-3 mb-0 d-flex align-items-center">
                <i className="bx bx-error-circle me-2 fs-4"></i>
                <div>
                  <div className="fw-semibold">
                    {t('cost_tracking.asset_tco.replacement_banner_title', 'Replacement Suggested')}
                  </div>
                  <small>
                    {t('cost_tracking.asset_tco.replacement_banner_body',
                      'Cumulative maintenance has reached {{percent}}% of purchase price.',
                      { percent: tco.replacementThresholdPercent != null
                          ? Number(tco.replacementThresholdPercent).toFixed(0)
                          : '50' })}
                  </small>
                </div>
              </div>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default AssetTCOPanel;
